import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Kicker } from "@/components/Primitives";

const KPI_KEYS = [
  ["total_candidates", "admin.kpi.total"],
  ["new_candidates", "admin.kpi.new"],
  ["active_candidates", "admin.kpi.active"],
  ["unassigned_candidates", "admin.kpi.unassigned"],
];

export default function AdminDashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (!data) {
    return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;
  }

  return (
    <div data-testid="admin-dashboard">
      <Kicker>{data.scope === "assigned" ? t("admin.scopeAssigned") : t("admin.scopeAll")}</Kicker>
      <h1 className="mt-3 font-display text-3xl font-light sm:text-4xl">{t("admin.dashboard")}</h1>

      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {KPI_KEYS.map(([key, label]) => (
          <div key={key} data-testid={`kpi-${key}`} className="border border-border bg-surface p-5 transition-transform duration-300 hover:-translate-y-1">
            <p className="cs-kicker">{t(label)}</p>
            <p className="mt-3 font-sans text-4xl font-light tabular-nums">{data[key] ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <section data-testid="dashboard-by-stage" className="lg:col-span-7 border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.byStage")}</h2>
          <ul className="mt-4 space-y-2">
            {Object.entries(data.by_stage || {}).map(([stage, count]) => (
              <li key={stage} className="flex items-center gap-3 text-sm">
                <span className="w-44 shrink-0 truncate text-muted-foreground">{t(`stages.${stage}`)}</span>
                <span className="h-2 flex-1 bg-muted">
                  <span
                    className="block h-2 bg-accent"
                    style={{ width: `${data.total_candidates ? (count / data.total_candidates) * 100 : 0}%` }}
                  />
                </span>
                <span className="w-8 text-right tabular-nums">{count}</span>
              </li>
            ))}
          </ul>
        </section>

        <section data-testid="dashboard-by-vertical" className="lg:col-span-5 border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.byVertical")}</h2>
          <ul className="mt-4 space-y-2">
            {Object.entries(data.by_vertical || {}).map(([vertical, count]) => (
              <li key={vertical} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-muted-foreground">{t(`verticals.${vertical}`)}</span>
                <span className="tabular-nums">{count}</span>
              </li>
            ))}
          </ul>
        </section>

        <section data-testid="dashboard-recent" className="lg:col-span-7 border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.recent")}</h2>
          <ul className="mt-4 divide-y divide-border">
            {(data.recent_candidates || []).map((candidate) => (
              <li key={candidate.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                <Link to={`/${lang}/admin/candidates/${candidate.id}`} className="hover:text-accent">
                  {candidate.first_name} {candidate.last_name}
                </Link>
                <span className="text-xs text-muted-foreground">{t(`stages.${candidate.pipeline_stage}`)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section data-testid="dashboard-tasks" className="lg:col-span-5 border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.tasksDue")}</h2>
          {(data.tasks_due || []).length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">{t("admin.noTasks")}</p>
          ) : (
            <ul className="mt-4 divide-y divide-border">
              {data.tasks_due.map((task) => (
                <li key={task.id} className="py-3 text-sm">
                  <Link to={`/${lang}/admin/candidates/${task.candidate_id}`} className="hover:text-accent">
                    {task.title}
                  </Link>
                  {task.due_date && <span className="ml-2 text-xs text-muted-foreground">{task.due_date}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="dashboard-geo" className="lg:col-span-6 border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.byGeo")}</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {(data.by_geography || []).map((row, index) => (
              <li key={index} className="flex justify-between gap-3">
                <span className="text-muted-foreground">
                  {row.province_state ? `${row.province_state}, ` : ""}
                  {row.country || "—"}
                </span>
                <span className="tabular-nums">{row.count}</span>
              </li>
            ))}
          </ul>
        </section>

        <section data-testid="dashboard-source" className="lg:col-span-6 border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.bySource")}</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {(data.by_source || []).map((row, index) => (
              <li key={index} className="flex justify-between gap-3">
                <span className="text-muted-foreground">{row.source}</span>
                <span className="tabular-nums">{row.count}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
      <p className="mt-8 text-xs text-muted-foreground">{user?.role} · Phase 1</p>
    </div>
  );
}
